// src/cockpit4/components/ContractManager.tsx
// ============================================
// P1-功能7：合同与订单管理（客户中心）
//
// 功能：
//   1. 合同模板库（内置 3-5 个合同模板：服务合同/销售合同/技术服务合同等）
//   2. 合同自动生成（选择客户+模板，生成合同 Markdown，写入 Vault：XEAOS/CRM/Contracts/{客户名}-{date}.md）
//   3. 合同审批流程（待审批/已通过/已拒绝 三态管理）
//   4. 订单管理和追踪（订单列表：金额/状态/交付进度）
//   5. 回款管理和提醒（回款计划：应收/已收/逾期）
//   6. 发票管理（发票记录：金额/抬头/状态）
//
// 数据来源：
//   - localStorage（key: xeaos_contracts）持久化合同/订单/回款/发票
//   - RuntimeManager.query(RuntimeName.CRM, { type: "list" }) 获取客户
//   - 无数据时显示空状态提示，绝不展示假数据
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileSignature,
  RefreshCw,
  Loader2,
  Users,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  Save,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Receipt,
  AlertTriangle,
  Plus,
  X,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import type { CustomerProfile } from "../../runtime/CRMRuntime/types";

// ============================================
// 类型与常量
// ============================================

const CONTRACTS_STORAGE_KEY = "xeaos_contracts";

/** 合同审批状态 */
type ApprovalStatus = "pending" | "approved" | "rejected";

/** 订单状态 */
type OrderStatus = "pending" | "in_progress" | "delivered" | "cancelled";

/** 回款状态 */
type PaymentStatus = "receivable" | "received" | "overdue";

/** 发票状态 */
type InvoiceStatus = "issued" | "paid" | "cancelled";

/** 合同模板元数据 */
interface ContractTemplate {
  id: string;
  name: string;
  description: string;
  color: string;
  /** 生成合同正文的模板函数 */
  build: (ctx: {
    customer: CustomerProfile;
    amount: number;
    date: string;
    validUntil: string;
  }) => string;
}

/** 合同记录 */
interface ContractRecord {
  id: string;
  customerId: string;
  customerName: string;
  templateId: string;
  templateName: string;
  amount: number;
  approvalStatus: ApprovalStatus;
  createdAt: string;
  validUntil: string;
  vaultPath?: string;
  /** 关联订单 */
  orders: OrderRecord[];
  /** 回款计划 */
  payments: PaymentPlan[];
  /** 发票记录 */
  invoices: InvoiceRecord[];
}

/** 订单记录 */
interface OrderRecord {
  id: string;
  name: string;
  amount: number;
  status: OrderStatus;
  progress: number; // 0-100
  createdAt: string;
}

/** 回款计划 */
interface PaymentPlan {
  id: string;
  title: string;
  amount: number;
  dueDate: string;
  status: PaymentStatus;
  receivedDate?: string;
}

/** 发票记录 */
interface InvoiceRecord {
  id: string;
  title: string;
  amount: number;
  taxRate: number;
  invoiceTitle: string;
  status: InvoiceStatus;
  issueDate: string;
}

// ============================================
// 内置合同模板库（非客户数据，是文档模板）
// ============================================

const CONTRACT_TEMPLATES: ContractTemplate[] = [
  {
    id: "service",
    name: "服务合同",
    description: "适用于 SaaS/订阅/年度服务合作",
    color: "#3B82F6",
    build: (ctx) => `# 服务合同 · ${ctx.customer.company}

> 由 XEAOS 客户中心 · 合同管理生成
> 签订日期：${ctx.date} · 有效期至：${ctx.validUntil}

## 甲方（服务提供方）
XX 科技有限公司

## 乙方（客户）
- 公司名称：${ctx.customer.company}
- 行业：${ctx.customer.industry || "—"}
- 地区：${ctx.customer.region || "—"}
- 联系人：${ctx.customer.contact.name || "—"} / ${ctx.customer.contact.phone || "—"}

## 合同金额
人民币（大写）：详见附件报价单
小写：¥${ctx.amount.toLocaleString()}

## 服务内容
1. 提供约定的 SaaS 产品/年度服务使用权
2. 包含标准技术支持（工作日 9x5）
3. 提供产品使用培训与文档
4. 按约定周期交付服务报告

## 付款方式
按回款计划分期支付，详见回款计划表。

## 有效期
自 ${ctx.date} 起至 ${ctx.validUntil} 止，到期前 30 天可协商续约。

## 违约责任
任一方违约，违约方应赔偿守约方实际损失。

---
*本合同由 XEAOS 自动生成，正式签署以双方盖章为准。*
`,
  },
  {
    id: "sales",
    name: "销售合同",
    description: "适用于产品买卖/硬件销售",
    color: "#34D399",
    build: (ctx) => `# 销售合同 · ${ctx.customer.company}

> 由 XEAOS 客户中心 · 合同管理生成
> 签订日期：${ctx.date} · 有效期至：${ctx.validUntil}

## 甲方（卖方）
XX 科技有限公司

## 乙方（买方）
- 公司名称：${ctx.customer.company}
- 联系人：${ctx.customer.contact.name || "—"}

## 标的物
详见产品清单附件。

## 合同金额
小写：¥${ctx.amount.toLocaleString()}

## 交付方式
甲方在收到首付款后 15 个工作日内交付。

## 验收标准
按产品规格书约定，乙方应在收货后 7 日内完成验收。

## 付款方式
按回款计划分期支付。

## 质保期
自验收合格之日起 12 个月。

---
*本合同由 XEAOS 自动生成，正式签署以双方盖章为准。*
`,
  },
  {
    id: "tech-service",
    name: "技术服务合同",
    description: "适用于定制开发/技术外包/集成实施",
    color: "#A78BFA",
    build: (ctx) => `# 技术服务合同 · ${ctx.customer.company}

> 由 XEAOS 客户中心 · 合同管理生成
> 签订日期：${ctx.date} · 有效期至：${ctx.validUntil}

## 甲方（服务方）
XX 科技有限公司

## 乙方（委托方）
- 公司名称：${ctx.customer.company}
- 行业：${ctx.customer.industry || "—"}

## 服务内容
1. 按需求文档进行定制开发/系统集成
2. 提供项目实施与交付
3. 提供验收后的运维支持

## 合同金额
小写：¥${ctx.amount.toLocaleString()}

## 交付里程碑
按订单交付进度执行，详见订单管理。

## 验收方式
分阶段验收，每阶段交付物经乙方确认后视为该阶段验收通过。

## 知识产权
定制开发成果的知识产权归属按附件约定执行。

## 保密条款
双方对合作中获知的对方商业秘密负有保密义务。

---
*本合同由 XEAOS 自动生成，正式签署以双方盖章为准。*
`,
  },
  {
    id: "reseller",
    name: "渠道代理合同",
    description: "适用于渠道合作/代理经销",
    color: "#FB923C",
    build: (ctx) => `# 渠道代理合同 · ${ctx.customer.company}

> 由 XEAOS 客户中心 · 合同管理生成
> 签订日期：${ctx.date} · 有效期至：${ctx.validUntil}

## 甲方（授权方）
XX 科技有限公司

## 乙方（代理方）
- 公司名称：${ctx.customer.company}
- 地区：${ctx.customer.region || "—"}

## 授权范围
甲方授权乙方在约定区域内销售甲方指定产品。

## 年度采购承诺
首年度采购金额不低于 ¥${ctx.amount.toLocaleString()}。

## 返利政策
达成年度采购承诺的，按约定比例返利。

## 价格政策
按甲方官方价格体系执行，乙方享受渠道折扣。

## 有效期
自 ${ctx.date} 起至 ${ctx.validUntil} 止。

---
*本合同由 XEAOS 自动生成，正式签署以双方盖章为准。*
`,
  },
  {
    id: "nda",
    name: "保密协议（NDA）",
    description: "适用于合作前信息交换/技术评估",
    color: "#9CA3AF",
    build: (ctx) => `# 保密协议 · ${ctx.customer.company}

> 由 XEAOS 客户中心 · 合同管理生成
> 签订日期：${ctx.date}

## 协议方
- 甲方：XX 科技有限公司
- 乙方：${ctx.customer.company}

## 保密信息定义
双方在合作过程中披露的任何非公开信息，包括但不限于技术方案、商业计划、客户数据、报价等。

## 保密义务
接收方应对保密信息严格保密，未经披露方书面同意不得向第三方披露。

## 保密期限
本协议自签订之日起长期有效，保密义务在合作终止后 3 年内继续有效。

## 违约责任
违反本协议的一方应赔偿对方因此造成的全部损失。

---
*本协议由 XEAOS 自动生成，正式签署以双方盖章为准。*
`,
  },
];

const APPROVAL_LABEL: Record<ApprovalStatus, string> = {
  pending: "待审批",
  approved: "已通过",
  rejected: "已拒绝",
};

const APPROVAL_COLOR: Record<ApprovalStatus, string> = {
  pending: "#FBBF24",
  approved: "#34D399",
  rejected: "#F87171",
};

const ORDER_LABEL: Record<OrderStatus, string> = {
  pending: "待启动",
  in_progress: "进行中",
  delivered: "已交付",
  cancelled: "已取消",
};

const ORDER_COLOR: Record<OrderStatus, string> = {
  pending: "#9CA3AF",
  in_progress: "#60A5FA",
  delivered: "#34D399",
  cancelled: "#F87171",
};

const PAYMENT_LABEL: Record<PaymentStatus, string> = {
  receivable: "应收",
  received: "已收",
  overdue: "逾期",
};

const PAYMENT_COLOR: Record<PaymentStatus, string> = {
  receivable: "#FBBF24",
  received: "#34D399",
  overdue: "#F87171",
};

const INVOICE_LABEL: Record<InvoiceStatus, string> = {
  issued: "已开票",
  paid: "已付款",
  cancelled: "已作废",
};

const INVOICE_COLOR: Record<InvoiceStatus, string> = {
  issued: "#60A5FA",
  paid: "#34D399",
  cancelled: "#6B7280",
};

// ============================================
// 工具函数
// ============================================

const DAY_MS = 24 * 60 * 60 * 1000;

function genId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysUntil(dateStr: string): number {
  if (!dateStr) return Number.MAX_SAFE_INTEGER;
  const t = new Date(dateStr).getTime();
  if (isNaN(t)) return Number.MAX_SAFE_INTEGER;
  return Math.floor((t - Date.now()) / DAY_MS);
}

function formatAmount(amount: number): string {
  if (!amount || amount <= 0) return "¥0";
  if (amount >= 10000) return `¥${(amount / 10000).toFixed(1)}w`;
  return `¥${amount.toLocaleString()}`;
}

/** 安全读取 localStorage */
function loadContracts(): ContractRecord[] {
  try {
    const raw = localStorage.getItem(CONTRACTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ContractRecord[]) : [];
  } catch {
    return [];
  }
}

/** 安全写入 localStorage */
function saveContracts(contracts: ContractRecord[]): void {
  try {
    localStorage.setItem(CONTRACTS_STORAGE_KEY, JSON.stringify(contracts));
  } catch {
    // 容量超限或隐私模式：忽略
  }
}

/** 通过 /vault-api/write 写入 Vault */
async function writeVaultContract(path: string, content: string): Promise<void> {
  const resp = await fetch("/vault-api/write", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ path, content }),
  });
  const data = await resp.json().catch(() => null);
  if (!resp.ok || !data?.success) {
    throw new Error(data?.error || `写入 Vault 失败（HTTP ${resp.status}）`);
  }
}

// ============================================
// 主组件
// ============================================

export default function ContractManager() {
  const [customers, setCustomers] = useState<CustomerProfile[]>([]);
  const [contracts, setContracts] = useState<ContractRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 新建合同表单
  const [showForm, setShowForm] = useState(false);
  const [formCustomerId, setFormCustomerId] = useState("");
  const [formTemplateId, setFormTemplateId] = useState("");
  const [formAmount, setFormAmount] = useState("");
  const [generating, setGenerating] = useState(false);
  const [genMsg, setGenMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // 展开的合同 ID
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // 新增订单/回款/发票的草稿（按 contractId_kind）
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  // 加载真实客户列表
  const loadCustomers = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      for (let i = 0; i < 30; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 300));
      }
      if (!RuntimeManager.isInitialized()) {
        setError("RuntimeManager 尚未初始化");
        setLoading(false);
        return;
      }
      const resp = await RuntimeManager.query(RuntimeName.CRM, { type: "list" });
      if (resp?.success && resp.data) {
        const inner = resp.data as { data?: CustomerProfile[] } | CustomerProfile[];
        const list = Array.isArray(inner) ? inner : inner?.data || [];
        setCustomers(Array.isArray(list) ? list : []);
      } else {
        setError(resp?.error || "查询客户列表失败");
      }
    } catch (err) {
      setError((err as Error).message || "加载客户列表异常");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // 初次挂载
  useEffect(() => {
    setContracts(loadContracts());
    void loadCustomers();
  }, [loadCustomers]);

  // 合同变化时持久化
  useEffect(() => {
    saveContracts(contracts);
  }, [contracts]);

  // 选中的客户与模板
  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === formCustomerId) || null,
    [customers, formCustomerId]
  );
  const selectedTemplate = useMemo(
    () => CONTRACT_TEMPLATES.find((t) => t.id === formTemplateId) || null,
    [formTemplateId]
  );

  /** 生成合同并写入 Vault */
  const generateContract = useCallback(async () => {
    if (!selectedCustomer || !selectedTemplate) return;
    const amount = Number(formAmount);
    if (!amount || amount <= 0) {
      setGenMsg({ ok: false, text: "请输入有效合同金额" });
      return;
    }
    setGenerating(true);
    setGenMsg(null);
    try {
      const today = todayStr();
      const validUntilDate = new Date();
      validUntilDate.setDate(validUntilDate.getDate() + 365);
      const validUntil = validUntilDate.toISOString().slice(0, 10);

      const markdown = selectedTemplate.build({
        customer: selectedCustomer,
        amount,
        date: today,
        validUntil,
      });

      const safeName = selectedCustomer.company.replace(/[\\/:*?"<>|]/g, "_");
      const vaultPath = `XEAOS/CRM/Contracts/${safeName}-${today}.md`;
      await writeVaultContract(vaultPath, markdown);

      const contractId = genId("contract");
      // 默认生成 3 期回款计划（30% 首付 + 40% 进度款 + 30% 尾款）
      const payments: PaymentPlan[] = [
        {
          id: genId("pay"),
          title: "首付款（30%）",
          amount: Math.round(amount * 0.3),
          dueDate: today,
          status: "receivable",
        },
        {
          id: genId("pay"),
          title: "进度款（40%）",
          amount: Math.round(amount * 0.4),
          dueDate: (() => {
            const d = new Date();
            d.setDate(d.getDate() + 30);
            return d.toISOString().slice(0, 10);
          })(),
          status: "receivable",
        },
        {
          id: genId("pay"),
          title: "尾款（30%）",
          amount: Math.round(amount * 0.3),
          dueDate: validUntil,
          status: "receivable",
        },
      ];

      const contract: ContractRecord = {
        id: contractId,
        customerId: selectedCustomer.id,
        customerName: selectedCustomer.company,
        templateId: selectedTemplate.id,
        templateName: selectedTemplate.name,
        amount,
        approvalStatus: "pending",
        createdAt: today,
        validUntil,
        vaultPath,
        orders: [],
        payments,
        invoices: [],
      };
      setContracts((prev) => [contract, ...prev]);
      setGenMsg({ ok: true, text: `已生成合同并写入 Vault：${vaultPath}` });
      setFormCustomerId("");
      setFormTemplateId("");
      setFormAmount("");
      setShowForm(false);
    } catch (err) {
      setGenMsg({ ok: false, text: `生成失败：${(err as Error).message || String(err)}` });
    } finally {
      setGenerating(false);
    }
  }, [selectedCustomer, selectedTemplate, formAmount]);

  /** 更新合同审批状态 */
  const updateApproval = useCallback((contractId: string, status: ApprovalStatus) => {
    setContracts((prev) => prev.map((c) => (c.id === contractId ? { ...c, approvalStatus: status } : c)));
  }, []);

  /** 新增订单 */
  const addOrder = useCallback((contractId: string) => {
    const name = drafts[`${contractId}_order`];
    if (!name || !name.trim()) return;
    const order: OrderRecord = {
      id: genId("order"),
      name: name.trim(),
      amount: 0,
      status: "pending",
      progress: 0,
      createdAt: todayStr(),
    };
    setContracts((prev) =>
      prev.map((c) => (c.id === contractId ? { ...c, orders: [...c.orders, order] } : c))
    );
    setDrafts((s) => {
      const next = { ...s };
      delete next[`${contractId}_order`];
      return next;
    });
  }, [drafts]);

  /** 更新订单状态/进度 */
  const updateOrder = useCallback(
    (contractId: string, orderId: string, updates: Partial<OrderRecord>) => {
      setContracts((prev) =>
        prev.map((c) =>
          c.id === contractId
            ? {
                ...c,
                orders: c.orders.map((o) => (o.id === orderId ? { ...o, ...updates } : o)),
              }
            : c
        )
      );
    },
    []
  );

  /** 删除订单 */
  const removeOrder = useCallback((contractId: string, orderId: string) => {
    setContracts((prev) =>
      prev.map((c) =>
        c.id === contractId ? { ...c, orders: c.orders.filter((o) => o.id !== orderId) } : c
      )
    );
  }, []);

  /** 标记回款为已收 */
  const markPaymentReceived = useCallback((contractId: string, paymentId: string) => {
    setContracts((prev) =>
      prev.map((c) =>
        c.id === contractId
          ? {
              ...c,
              payments: c.payments.map((p) =>
                p.id === paymentId ? { ...p, status: "received", receivedDate: todayStr() } : p
              ),
            }
          : c
      )
    );
  }, []);

  /** 新增发票 */
  const addInvoice = useCallback((contractId: string) => {
    const title = drafts[`${contractId}_invoice`];
    if (!title || !title.trim()) return;
    const invoice: InvoiceRecord = {
      id: genId("inv"),
      title: title.trim(),
      amount: 0,
      taxRate: 6,
      invoiceTitle: "",
      status: "issued",
      issueDate: todayStr(),
    };
    setContracts((prev) =>
      prev.map((c) => (c.id === contractId ? { ...c, invoices: [...c.invoices, invoice] } : c))
    );
    setDrafts((s) => {
      const next = { ...s };
      delete next[`${contractId}_invoice`];
      return next;
    });
  }, [drafts]);

  /** 更新发票 */
  const updateInvoice = useCallback(
    (contractId: string, invoiceId: string, updates: Partial<InvoiceRecord>) => {
      setContracts((prev) =>
        prev.map((c) =>
          c.id === contractId
            ? {
                ...c,
                invoices: c.invoices.map((i) => (i.id === invoiceId ? { ...i, ...updates } : i)),
              }
            : c
        )
      );
    },
    []
  );

  /** 删除发票 */
  const removeInvoice = useCallback((contractId: string, invoiceId: string) => {
    setContracts((prev) =>
      prev.map((c) =>
        c.id === contractId ? { ...c, invoices: c.invoices.filter((i) => i.id !== invoiceId) } : c
      )
    );
  }, []);

  /** 删除合同 */
  const removeContract = useCallback((contractId: string) => {
    setContracts((prev) => prev.filter((c) => c.id !== contractId));
  }, []);

  // 概览统计
  const overview = useMemo(() => {
    const total = contracts.length;
    const pending = contracts.filter((c) => c.approvalStatus === "pending").length;
    const approved = contracts.filter((c) => c.approvalStatus === "approved").length;
    const totalAmount = contracts.reduce((s, c) => s + c.amount, 0);
    // 回款统计
    let receivable = 0;
    let received = 0;
    let overdue = 0;
    for (const c of contracts) {
      for (const p of c.payments) {
        if (p.status === "received") received += p.amount;
        else if (p.status === "overdue") overdue += p.amount;
        else receivable += p.amount;
      }
    }
    return { total, pending, approved, totalAmount, receivable, received, overdue };
  }, [contracts]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-cyan-500/15 border border-cyan-500/30">
            <FileSignature size={13} className="text-cyan-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">合同与订单管理</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Contract Manager · {overview.total} 合同 · 待审批 {overview.pending} · 已收 {formatAmount(overview.received)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowForm((v) => !v)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors"
          >
            <Plus size={11} className="text-cyan-400" />
            <span className="text-[9px] font-mono text-cyan-300 uppercase tracking-wider">新建合同</span>
          </button>
          <button
            onClick={loadCustomers}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors disabled:opacity-50"
          >
            {refreshing ? (
              <Loader2 size={11} className="text-cyan-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-cyan-400" />
            )}
            <span className="text-[9px] font-mono text-cyan-300 uppercase tracking-wider">
              {refreshing ? "刷新中..." : "刷新"}
            </span>
          </button>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div className="px-3 py-2 rounded-md border border-red-500/30 bg-red-500/[0.06] text-red-300 text-[10px] font-mono">
            {error}
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center">
          <Loader2 size={20} className="mx-auto text-cyan-400 animate-spin" />
          <p className="text-[10px] font-mono text-gray-600 mt-2">加载客户数据...</p>
        </div>
      ) : (
        <>
          {/* 顶部 KPI */}
          <div className="grid grid-cols-4 gap-2 p-4 border-b border-white/[0.04]">
            <KpiCard icon={FileSignature} label="合同总数" value={String(overview.total)} color="#38BDF8" />
            <KpiCard icon={Clock} label="待审批" value={String(overview.pending)} color="#FBBF24" />
            <KpiCard icon={DollarSign} label="合同总金额" value={formatAmount(overview.totalAmount)} color="#34D399" />
            <KpiCard icon={AlertTriangle} label="逾期应收" value={formatAmount(overview.overdue)} color="#F87171" />
          </div>

          {/* 回款概览 */}
          {contracts.length > 0 && (
            <div className="grid grid-cols-3 gap-2 px-4 py-2 border-b border-white/[0.04]">
              <PaymentSummary label="应收未收" value={overview.receivable} color="#FBBF24" />
              <PaymentSummary label="已收款" value={overview.received} color="#34D399" />
              <PaymentSummary label="逾期款" value={overview.overdue} color="#F87171" />
            </div>
          )}

          {/* 新建合同表单 */}
          <AnimatePresence>
            {showForm && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden border-b border-white/[0.04]"
              >
                <div className="p-4 space-y-2 bg-white/[0.01]">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-mono text-cyan-400 uppercase tracking-wider">
                      新建合同（选择客户 + 模板 → 生成 Markdown 写入 Vault）
                    </div>
                    <button
                      onClick={() => setShowForm(false)}
                      className="text-gray-500 hover:text-gray-300"
                    >
                      <X size={12} />
                    </button>
                  </div>
                  {customers.length === 0 ? (
                    <p className="text-[11px] font-mono text-gray-600 text-center py-2">暂无客户数据</p>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={formCustomerId}
                          onChange={(e) => setFormCustomerId(e.target.value)}
                          className="px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                        >
                          <option value="">— 选择客户 —</option>
                          {customers.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.company} · {c.tier}
                            </option>
                          ))}
                        </select>
                        <select
                          value={formTemplateId}
                          onChange={(e) => setFormTemplateId(e.target.value)}
                          className="px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                        >
                          <option value="">— 选择合同模板 —</option>
                          {CONTRACT_TEMPLATES.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name} · {t.description}
                            </option>
                          ))}
                        </select>
                      </div>
                      <input
                        value={formAmount}
                        onChange={(e) => setFormAmount(e.target.value)}
                        placeholder="合同金额（元）*"
                        className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                      />
                      {/* 模板预览 */}
                      {selectedTemplate && (
                        <div className="rounded bg-white/[0.02] border border-white/[0.04] p-2">
                          <div className="flex items-center gap-1.5 mb-1">
                            <span
                              className="w-1.5 h-1.5 rounded-full"
                              style={{ background: selectedTemplate.color }}
                            />
                            <span
                              className="text-[10px] font-mono uppercase"
                              style={{ color: selectedTemplate.color }}
                            >
                              {selectedTemplate.name}
                            </span>
                          </div>
                          <div className="text-[9px] font-mono text-gray-500">{selectedTemplate.description}</div>
                        </div>
                      )}
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setShowForm(false)}
                          className="px-2 py-1 rounded text-[9px] font-mono text-gray-400 hover:text-gray-200"
                        >
                          取消
                        </button>
                        <button
                          onClick={() => void generateContract()}
                          disabled={generating || !selectedCustomer || !selectedTemplate || !formAmount}
                          className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {generating ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />}
                          生成合同
                        </button>
                      </div>
                      {genMsg && (
                        <div
                          className={`px-2 py-1 rounded text-[9px] font-mono border ${
                            genMsg.ok
                              ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
                              : "border-red-500/30 bg-red-500/[0.06] text-red-300"
                          }`}
                        >
                          {genMsg.text}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* 合同模板库预览 */}
          <div className="p-4 border-b border-white/[0.04]">
            <div className="flex items-center gap-2 mb-2">
              <FileText size={11} className="text-cyan-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                合同模板库（{CONTRACT_TEMPLATES.length}）
              </span>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {CONTRACT_TEMPLATES.map((t) => (
                <div
                  key={t.id}
                  className="rounded border p-1.5"
                  style={{ borderColor: `${t.color}30`, background: `${t.color}08` }}
                >
                  <div className="text-[10px] font-mono font-semibold" style={{ color: t.color }}>
                    {t.name}
                  </div>
                  <div className="text-[8px] font-mono text-gray-500 mt-0.5 leading-tight">
                    {t.description}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 合同列表 */}
          <div className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileSignature size={11} className="text-cyan-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                合同列表（{contracts.length}）
              </span>
            </div>
            {contracts.length === 0 ? (
              <div className="py-8 text-center">
                <Users size={22} className="mx-auto text-gray-600 mb-2" />
                <p className="text-[11px] font-mono text-gray-500">暂无合同记录，点击「新建合同」开始</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {contracts.map((c) => {
                  const isExpanded = expandedId === c.id;
                  // 回款进度
                  const paidAmount = c.payments.filter((p) => p.status === "received").reduce((s, p) => s + p.amount, 0);
                  const paymentProgress = c.amount > 0 ? Math.round((paidAmount / c.amount) * 100) : 0;
                  // 订单进度
                  const orderProgress =
                    c.orders.length > 0
                      ? Math.round(c.orders.reduce((s, o) => s + o.progress, 0) / c.orders.length)
                      : 0;
                  return (
                    <div
                      key={c.id}
                      className="rounded-md border border-white/[0.04] bg-white/[0.02] overflow-hidden"
                    >
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : c.id)}
                        className="w-full flex items-center gap-2.5 p-2.5 text-left hover:bg-white/[0.03] transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[12px] text-gray-200 truncate">{c.customerName}</span>
                            <span
                              className="px-1 rounded text-[8px] font-mono"
                              style={{ background: `${APPROVAL_COLOR[c.approvalStatus]}20`, color: APPROVAL_COLOR[c.approvalStatus] }}
                            >
                              {APPROVAL_LABEL[c.approvalStatus]}
                            </span>
                            <span className="text-[9px] font-mono text-gray-500">{c.templateName}</span>
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5 text-[9px] font-mono text-gray-500">
                            <span>{c.createdAt}</span>
                            <span>·</span>
                            <span>至 {c.validUntil}</span>
                            {c.vaultPath && (
                              <>
                                <span>·</span>
                                <span className="text-cyan-500 truncate max-w-[160px]" title={c.vaultPath}>
                                  {c.vaultPath}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <div className="text-[12px] font-mono font-bold text-emerald-400">
                            {formatAmount(c.amount)}
                          </div>
                          <div className="text-[9px] font-mono text-gray-500">
                            回款 {paymentProgress}% · 订单 {orderProgress}%
                          </div>
                        </div>
                        {isExpanded ? (
                          <ChevronDown size={11} className="text-gray-500 flex-shrink-0" />
                        ) : (
                          <ChevronRight size={11} className="text-gray-500 flex-shrink-0" />
                        )}
                      </button>

                      {/* 展开内容：审批 + 订单 + 回款 + 发票 */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="border-t border-white/[0.04] overflow-hidden"
                          >
                            <div className="p-2.5 space-y-2.5">
                              {/* 审批状态切换 */}
                              <div className="flex items-center gap-1 flex-wrap">
                                <span className="text-[9px] font-mono text-gray-500 uppercase mr-1">审批：</span>
                                {(["pending", "approved", "rejected"] as ApprovalStatus[]).map((s) => {
                                  const Icon = s === "approved" ? CheckCircle2 : s === "rejected" ? XCircle : Clock;
                                  return (
                                    <button
                                      key={s}
                                      onClick={() => updateApproval(c.id, s)}
                                      className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono transition-colors ${
                                        c.approvalStatus === s
                                          ? "bg-white/[0.08] text-white border border-white/15"
                                          : "text-gray-500 hover:text-gray-300 border border-transparent"
                                      }`}
                                    >
                                      <Icon size={9} style={{ color: APPROVAL_COLOR[s] }} />
                                      {APPROVAL_LABEL[s]}
                                    </button>
                                  );
                                })}
                                <button
                                  onClick={() => removeContract(c.id)}
                                  className="ml-auto px-2 py-0.5 rounded text-[9px] font-mono text-gray-500 hover:text-red-400"
                                >
                                  删除合同
                                </button>
                              </div>

                              {/* 订单管理 */}
                              <div>
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  <FileText size={10} className="text-blue-400" />
                                  <span className="text-[9px] font-mono text-gray-500 uppercase">
                                    订单管理（{c.orders.length}）
                                  </span>
                                </div>
                                {c.orders.length > 0 && (
                                  <div className="space-y-1 mb-2">
                                    {c.orders.map((o) => (
                                      <div
                                        key={o.id}
                                        className="rounded bg-white/[0.02] border border-white/[0.04] p-1.5"
                                      >
                                        <div className="flex items-center gap-2 text-[10px] font-mono">
                                          <span className="text-gray-300 flex-1 truncate">{o.name}</span>
                                          <select
                                            value={o.status}
                                            onChange={(e) =>
                                              updateOrder(c.id, o.id, { status: e.target.value as OrderStatus })
                                            }
                                            className="px-1 py-0.5 rounded bg-white/[0.03] border border-white/[0.06] text-[9px] text-gray-300 font-mono"
                                            style={{ color: ORDER_COLOR[o.status] }}
                                          >
                                            {(["pending", "in_progress", "delivered", "cancelled"] as OrderStatus[]).map((s) => (
                                              <option key={s} value={s}>
                                                {ORDER_LABEL[s]}
                                              </option>
                                            ))}
                                          </select>
                                          <button
                                            onClick={() => removeOrder(c.id, o.id)}
                                            className="text-gray-600 hover:text-red-400"
                                          >
                                            <X size={9} />
                                          </button>
                                        </div>
                                        <div className="flex items-center gap-2 mt-1">
                                          <span className="text-[9px] font-mono text-gray-500">进度</span>
                                          <input
                                            type="range"
                                            min={0}
                                            max={100}
                                            value={o.progress}
                                            onChange={(e) =>
                                              updateOrder(c.id, o.id, {
                                                progress: Number(e.target.value),
                                                status: Number(e.target.value) === 100 ? "delivered" : Number(e.target.value) > 0 ? "in_progress" : "pending",
                                              })
                                            }
                                            className="flex-1 h-1"
                                          />
                                          <span className="text-[9px] font-mono text-gray-400 w-8 text-right">
                                            {o.progress}%
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <div className="flex items-center gap-1.5">
                                  <input
                                    value={drafts[`${c.id}_order`] || ""}
                                    onChange={(e) =>
                                      setDrafts((s) => ({ ...s, [`${c.id}_order`]: e.target.value }))
                                    }
                                    placeholder="新增订单名称"
                                    className="flex-1 px-2 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-[10px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                                  />
                                  <button
                                    onClick={() => addOrder(c.id)}
                                    disabled={!drafts[`${c.id}_order`]}
                                    className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-blue-500/15 border border-blue-500/30 text-blue-300 hover:bg-blue-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    <Plus size={10} />
                                    添加订单
                                  </button>
                                </div>
                              </div>

                              {/* 回款管理 */}
                              <div>
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  <DollarSign size={10} className="text-emerald-400" />
                                  <span className="text-[9px] font-mono text-gray-500 uppercase">
                                    回款计划（已收 {formatAmount(paidAmount)} / {formatAmount(c.amount)}）
                                  </span>
                                </div>
                                <div className="space-y-1">
                                  {c.payments.map((p) => {
                                    const daysLeft = daysUntil(p.dueDate);
                                    const isOverdue = p.status !== "received" && daysLeft < 0;
                                    const effectiveStatus: PaymentStatus = isOverdue && p.status === "receivable" ? "overdue" : p.status;
                                    return (
                                      <div
                                        key={p.id}
                                        className="flex items-center gap-2 rounded bg-white/[0.02] border border-white/[0.04] p-1.5 text-[10px] font-mono"
                                      >
                                        <span className="flex-1 text-gray-300 truncate">{p.title}</span>
                                        <span className="text-emerald-400">{formatAmount(p.amount)}</span>
                                        <span className="text-gray-500">{p.dueDate}</span>
                                        <span
                                          className="px-1 rounded text-[8px]"
                                          style={{ background: `${PAYMENT_COLOR[effectiveStatus]}20`, color: PAYMENT_COLOR[effectiveStatus] }}
                                        >
                                          {PAYMENT_LABEL[effectiveStatus]}
                                        </span>
                                        {p.status !== "received" && (
                                          <button
                                            onClick={() => markPaymentReceived(c.id, p.id)}
                                            className="px-1 py-0.5 rounded text-[8px] text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20"
                                          >
                                            标记已收
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* 发票管理 */}
                              <div>
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  <Receipt size={10} className="text-amber-400" />
                                  <span className="text-[9px] font-mono text-gray-500 uppercase">
                                    发票记录（{c.invoices.length}）
                                  </span>
                                </div>
                                {c.invoices.length > 0 && (
                                  <div className="space-y-1 mb-2">
                                    {c.invoices.map((inv) => (
                                      <div
                                        key={inv.id}
                                        className="rounded bg-white/[0.02] border border-white/[0.04] p-1.5 text-[10px] font-mono"
                                      >
                                        <div className="flex items-center gap-2">
                                          <input
                                            value={inv.title}
                                            onChange={(e) => updateInvoice(c.id, inv.id, { title: e.target.value })}
                                            className="flex-1 px-1 py-0.5 rounded bg-white/[0.03] border border-white/[0.06] text-gray-300 font-mono text-[10px] focus:outline-none focus:border-amber-500/40"
                                          />
                                          <button
                                            onClick={() => removeInvoice(c.id, inv.id)}
                                            className="text-gray-600 hover:text-red-400"
                                          >
                                            <X size={9} />
                                          </button>
                                        </div>
                                        <div className="grid grid-cols-3 gap-1.5 mt-1">
                                          <input
                                            type="number"
                                            value={inv.amount || ""}
                                            onChange={(e) => updateInvoice(c.id, inv.id, { amount: Number(e.target.value) || 0 })}
                                            placeholder="金额"
                                            className="px-1 py-0.5 rounded bg-white/[0.03] border border-white/[0.06] text-gray-300 font-mono text-[9px] focus:outline-none focus:border-amber-500/40"
                                          />
                                          <input
                                            value={inv.invoiceTitle}
                                            onChange={(e) => updateInvoice(c.id, inv.id, { invoiceTitle: e.target.value })}
                                            placeholder="发票抬头"
                                            className="px-1 py-0.5 rounded bg-white/[0.03] border border-white/[0.06] text-gray-300 font-mono text-[9px] focus:outline-none focus:border-amber-500/40"
                                          />
                                          <select
                                            value={inv.status}
                                            onChange={(e) => updateInvoice(c.id, inv.id, { status: e.target.value as InvoiceStatus })}
                                            className="px-1 py-0.5 rounded bg-white/[0.03] border border-white/[0.06] text-[9px] font-mono"
                                            style={{ color: INVOICE_COLOR[inv.status] }}
                                          >
                                            {(["issued", "paid", "cancelled"] as InvoiceStatus[]).map((s) => (
                                              <option key={s} value={s}>
                                                {INVOICE_LABEL[s]}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <div className="flex items-center gap-1.5">
                                  <input
                                    value={drafts[`${c.id}_invoice`] || ""}
                                    onChange={(e) =>
                                      setDrafts((s) => ({ ...s, [`${c.id}_invoice`]: e.target.value }))
                                    }
                                    placeholder="新增发票名称"
                                    className="flex-1 px-2 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-[10px] text-gray-200 font-mono focus:outline-none focus:border-amber-500/40"
                                  />
                                  <button
                                    onClick={() => addInvoice(c.id)}
                                    disabled={!drafts[`${c.id}_invoice`]}
                                    className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-amber-500/15 border border-amber-500/30 text-amber-300 hover:bg-amber-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    <Plus size={10} />
                                    添加发票
                                  </button>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </motion.div>
  );
}

// ============================================
// KPI 小卡片
// ============================================
interface KpiCardProps {
  icon: typeof FileSignature;
  label: string;
  value: string;
  color: string;
}

function KpiCard({ icon: Icon, label, value, color }: KpiCardProps) {
  return (
    <div
      className="rounded-md border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5 flex items-center gap-2"
      style={{ borderLeft: `2px solid ${color}` }}
    >
      <Icon size={13} style={{ color }} className="flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider truncate">{label}</div>
        <div className="text-[13px] font-mono font-semibold truncate" style={{ color }}>
          {value}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 回款汇总小卡片
// ============================================
function PaymentSummary({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div
      className="rounded-md border border-white/[0.04] bg-white/[0.02] px-2.5 py-1.5 flex items-center justify-between"
      style={{ borderLeft: `2px solid ${color}` }}
    >
      <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">{label}</span>
      <span className="text-[12px] font-mono font-semibold" style={{ color }}>
        {formatAmount(value)}
      </span>
    </div>
  );
}
